import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, useLayoutEffect, useRef } from "react"
import {
  getIsNavigatingHistory,
  getKey,
  getPopstateTimestamp,
  getScrollFromState,
  getScrollTimestamp,
  type HistoryState,
  type ScrollPos,
  setCurrentScrollHistory,
  setScrollToSessionStorage,
  getScrollFromSessionStorage,
  clearOldSessionStorageEntries,
  clearNonRootHistoryEntries,
} from "./storage"
import { isRecord } from "./util"
import { scrollRestoreConfig } from "./config"

const getWindowScroll = (): ScrollPos => [window.scrollX, window.scrollY]
const memoizationIntervalLimit = 601 //100 times per 30 seconds
const safariBugWorkaroundTimeThreshold = 2000 //Safari reset scroll position to 0 0 after popstate for some reason.

const getState = (): HistoryState => {
  const state = window.history.state as unknown
  return isRecord(state) ? state : null
}
const restoreScrollFromState = (
  state: HistoryState,
  pathname: string,
): void => {
  let scroll = getScrollFromState(state)
  if (scrollRestoreConfig.debug) {
    console.log(
      `Found scroll from state: ${scroll?.toString()}. ${window.location.href}`,
    )
  }

  // If no scroll found in state, try sessionStorage backup
  if (scroll === null) {
    scroll = getScrollFromSessionStorage(pathname)
    if (scrollRestoreConfig.debug) {
      console.log(
        `Found scroll from sessionStorage: ${scroll?.toString()}. ${window.location.href}`,
      )
    }
  }

  if (scroll !== null) {
    const [x, y] = scroll
    if (scrollRestoreConfig.debug) {
      console.log(
        `Scroll restored to ${x} ${y}. Document height ${window.document.body.clientHeight}.`,
      )
    }

    const canScroll = () => {
      // Check if document is ready and has enough content to scroll
      const bodyHeight = document.body.scrollHeight
      const bodyWidth = document.body.scrollWidth
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth

      // Check if we need to scroll and if it's possible
      const needsVerticalScroll = y > 0
      const needsHorizontalScroll = x > 0

      if (needsVerticalScroll && bodyHeight <= viewportHeight) {
        if (scrollRestoreConfig.debug) {
          console.log(
            `Cannot scroll vertically: content height (${bodyHeight}) <= viewport (${viewportHeight})`,
          )
        }
        return false
      }

      if (needsHorizontalScroll && bodyWidth <= viewportWidth) {
        if (scrollRestoreConfig.debug) {
          console.log(
            `Cannot scroll horizontally: content width (${bodyWidth}) <= viewport (${viewportWidth})`,
          )
        }
        return false
      }

      return true
    }

    const performScroll = () => {
      if (canScroll()) {
        window.scrollTo({
          behavior: "instant",
          left: x,
          top: y,
        })
      } else {
        if (scrollRestoreConfig.debug) {
          console.log(
            `Skipping scroll: Document not ready or insufficient content for target position (${x}, ${y})`,
          )
        }
      }
    }

    // Initial scroll attempt
    performScroll()

    // Continuously verify and correct scroll position for 60 frames
    let frameCount = 0
    const maxFrames = 60
    const tolerance = 1

    const verifyAndCorrectScroll = () => {
      frameCount++

      // Check current scroll position
      const currentX = window.scrollX
      const currentY = window.scrollY

      // If position doesn't match target, perform scroll again
      if (
        Math.abs(currentX - x) > tolerance ||
        Math.abs(currentY - y) > tolerance
      ) {
        if (scrollRestoreConfig.debug) {
          console.log(
            `Frame ${frameCount}/${maxFrames}: Position mismatch - current (${currentX}, ${currentY}) vs target (${x}, ${y}). Correcting...`,
          )
        }
        performScroll()
      } else if (frameCount === 1 || frameCount === maxFrames) {
        // Log success on first frame and last frame
        if (scrollRestoreConfig.debug) {
          console.log(
            `Frame ${frameCount}/${maxFrames}: Scroll position correct at (${currentX}, ${currentY})`,
          )
        }
      }

      // Continue checking until we reach max frames
      if (frameCount < maxFrames) {
        requestAnimationFrame(verifyAndCorrectScroll)
      } else {
        // Final check after all frames
        const finalX = window.scrollX
        const finalY = window.scrollY
        if (
          Math.abs(finalX - x) > tolerance ||
          Math.abs(finalY - y) > tolerance
        ) {
          if (scrollRestoreConfig.debug) {
            console.log(
              `Final position after ${maxFrames} frames: (${finalX}, ${finalY}) - still mismatched with target (${x}, ${y})`,
            )
          }
        } else {
          if (scrollRestoreConfig.debug) {
            console.log(
              `Scroll restoration completed successfully after ${maxFrames} frames at (${finalX}, ${finalY})`,
            )
          }
        }
      }
    }

    // Start verification and correction loop
    requestAnimationFrame(verifyAndCorrectScroll)

    if (scrollRestoreConfig.debug) {
      console.log(
        `Scroll is ${window.scrollX} ${window.scrollY} after restoring. ${window.innerHeight}`,
      )
    }
  }
}
const scrollMemoIntervalCountLimit = 2
const restoreCurrentScrollPosition = (pathname: string): void => {
  if (scrollRestoreConfig.debug) {
    console.log(`Restoring current scroll position. ${window.location.href}`)
  }
  restoreScrollFromState(getState(), pathname)
}
const defaultMemoInterval = 0
const numericTrue = 1
const defaultX = 0
const defaultY = 0
const useScrollRestorer = (): void => {
  const pathname = usePathname()
  const searchparams = useSearchParams()

  useLayoutEffect(() => {
    if (scrollRestoreConfig.debug) {
      console.log("Restoring based on hooks.")
    }
    restoreCurrentScrollPosition(pathname)

    // Clear non-root history entries when navigating to root
    if (pathname === "/") {
      if (scrollRestoreConfig.debug) {
        console.log("Navigated to root path, clearing non-root history entries")
      }
      clearNonRootHistoryEntries()
    }
  }, [pathname, searchparams])
  const scrollMemoTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const scrollMemoCountInInterval = useRef<number>(defaultMemoInterval) //Used to workaround instant scrollTo() calls.It's used to work around immediate scroll in tests and possible real world behaviour.
  const isSafariWorkaroundAllowedRef = useRef(false)
  useEffect(() => {
    window.history.scrollRestoration = "manual"

    // 모바일 크롬 감지 (한 번만 선언, iOS Chrome 포함)
    const isChromeMobile = /Chrome.*Mobile|CriOS/.test(navigator.userAgent)

    const navigationListener = ({ state: eState }: PopStateEvent): void => {
      if (scrollRestoreConfig.debug) {
        console.log("Popstate started.")
      }
      cancelDelayedScrollMemoization()

      isSafariWorkaroundAllowedRef.current = true
      const state = (isRecord(eState) ? eState : null) ?? {}
      window.history.replaceState(
        {
          ...state,
          [getKey("is_navigating_history")]: numericTrue,
          [getKey("popstate_timestamp")]: new Date().getTime(),
        },
        "",
      )
    }

    /**
     * This is important to run as late as possible after navigation.
     * We could use something like `setTimeout(restoreCurrentScroll,500)`, but this is not a reactive approach.
     * useLayoutEffect + usePageHref hook is the latest reactive thing Next.js app can provide to use.
     * In Safari even with `window.history.scrollRestoration = 'manual'` scroll position is reset.
     */
    const workaroundSafariBreaksScrollRestoration = ([
      x,
      y,
    ]: ScrollPos): boolean => {
      const state = getState()

      // Sometimes Safari scroll to the start because of unique behavior We restore it back.
      // This case cannot be tested with Playwright, or any other testing library.
      if (
        x === defaultX &&
        y === defaultY &&
        isSafariWorkaroundAllowedRef.current
      ) {
        const isWorkaroundAllowed = (() => {
          const timeNavigated = getPopstateTimestamp(state)
          if (timeNavigated === null) {
            return false
          }
          return (
            new Date().getTime() - timeNavigated <
            safariBugWorkaroundTimeThreshold
          )
        })() //Place here to prevent many computations
        const isNavHistory = getIsNavigatingHistory(state)
        if (scrollRestoreConfig.debug) {
          console.log(
            `Check workaround for safari: ${x} ${y} ${isWorkaroundAllowed}. Is popstate ${isNavHistory}. ${window.location.href}`,
          )
        }
        if (isWorkaroundAllowed && isNavHistory) {
          if (scrollRestoreConfig.debug) {
            console.log(
              `Reverting back scroll because browser tried to brake it..`,
            )
          }
          restoreCurrentScrollPosition(pathname)
          isSafariWorkaroundAllowedRef.current = false //Safari bug appears only once
          return true
        }
      }

      return false
    }
    const rememberScrollPosition = (pos: ScrollPos): void => {
      cancelDelayedScrollMemoization()
      setCurrentScrollHistory(pos)
      setScrollToSessionStorage(pathname, pos)
    }
    const unmountNavigationListener = (): void => {
      if (scrollRestoreConfig.debug) {
        console.log("Unmount popstate.")
      }

      window.removeEventListener("popstate", navigationListener)
    }
    const mountNavigationListener = (): void => {
      if (scrollRestoreConfig.debug) {
        console.log("Mount popstate.")
      }

      window.addEventListener("popstate", navigationListener, {
        passive: true,
      })
    }

    const cancelDelayedScrollMemoization = (): void => {
      if (scrollMemoTimeoutRef.current !== undefined) {
        if (scrollRestoreConfig.debug) {
          console.log(`Cancelled delayed memoization.`)
        }
        clearTimeout(scrollMemoTimeoutRef.current)
        scrollMemoTimeoutRef.current = undefined
      }
    }

    const scrollMemoizationHandler = (pos: ScrollPos): void => {
      const isScrollMemoAllowedNow = (): boolean => {
        const timestamp = getScrollTimestamp(getState())
        if (timestamp === null) {
          return true
        }
        return new Date().getTime() - timestamp > memoizationIntervalLimit
      }

      const isAllowedNow = isScrollMemoAllowedNow()
      if (scrollRestoreConfig.debug) {
        console.log(`Handle scroll event. Memo allowed: ${isAllowedNow}.`)
      }
      if (isAllowedNow) {
        scrollMemoCountInInterval.current = defaultMemoInterval
      }
      if (
        isAllowedNow ||
        scrollMemoCountInInterval.current < scrollMemoIntervalCountLimit
      ) {
        scrollMemoCountInInterval.current++
        rememberScrollPosition(pos)
      } else {
        if (scrollMemoTimeoutRef.current === undefined) {
          scrollMemoTimeoutRef.current = setTimeout(() => {
            rememberScrollPosition(pos)
            scrollMemoCountInInterval.current = defaultMemoInterval
            scrollMemoTimeoutRef.current = undefined
          }, memoizationIntervalLimit)
        }
      }
    }
    // 모바일 크롬 전용 throttle 적용
    let scrollThrottleTimeout: NodeJS.Timeout | null = null

    const scrollListener = (): void => {
      // 모바일 크롬에서 throttle 적용 (16ms = 60fps)
      if (isChromeMobile) {
        if (scrollThrottleTimeout) return

        scrollThrottleTimeout = setTimeout(() => {
          scrollThrottleTimeout = null
          executeScrollHandler()
        }, 16)
      } else {
        executeScrollHandler()
      }
    }

    const executeScrollHandler = (): void => {
      cancelDelayedScrollMemoization()
      const scroll = getWindowScroll()

      if (scrollRestoreConfig.debug) {
        console.log(`Scroll event ${scroll.toString()}. ${window.location.href}`)
      }
      workaroundSafariBreaksScrollRestoration(scroll)

      scrollMemoizationHandler(scroll)
    }

    const mountScrollListener = (): void => {
      if (scrollRestoreConfig.debug) {
        console.log("Scroll listener mounted.")
      }

      // 모바일 크롬에서 강화된 passive listener 설정
      const listenerOptions = {
        passive: true,
        capture: false,
        ...(isChromeMobile && {
          once: false, // 모바일 크롬에서 추가 안정성
        }),
      }

      window.addEventListener("scroll", scrollListener, listenerOptions)
    }

    const unmountScrollListener = (): void => {
      if (scrollRestoreConfig.debug) {
        console.log("Scroll listener unmounted.")
      }
      window.removeEventListener("scroll", scrollListener)

      // 모바일 크롬 throttle timeout 정리
      if (scrollThrottleTimeout) {
        clearTimeout(scrollThrottleTimeout)
        scrollThrottleTimeout = null
      }
    }
    // Clean old sessionStorage entries on mount
    clearOldSessionStorageEntries()

    mountNavigationListener()
    mountScrollListener()
    return () => {
      unmountNavigationListener()
      unmountScrollListener()
      cancelDelayedScrollMemoization()
    }
  }, [pathname])
}
export default useScrollRestorer
